@if (state) {
    <form
        novalidate
        autocomplete="off"
        class="flex flex-col gap-8"
        [ngClass]="{
            'rounded-lg border border-slate-200 bg-white p-8': !noContainer,
        }"
        [formGroup]="state.group"
    >
        <ng-template #sectionTemplate let-field="field" let-index="index">
            <div class="border-b border-slate-100 p-6 last:border-b-0">
                <!-- Section header with number badge -->
                <h2
                    class="mb-5 flex items-center gap-3 text-lg font-semibold text-slate-800"
                >
                    <span
                        class="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700"
                    >
                        {{ getSectionIndex(field) }}
                    </span>
                    <span>
                        {{
                            translateLabels
                                ? (asField(field).sectionTitle | translate)
                                : asField(field).sectionTitle
                        }}
                    </span>
                    @if (asField(field).isSectionRequired) {
                        <span class="text-rose-500"> * </span>
                    }
                </h2>
                <!-- Section note -->
                @if (asField(field).sectionNote) {
                    <p class="mb-4 text-sm text-slate-500">
                        {{ asField(field).sectionNote | translate }}
                    </p>
                }
                <!-- Section fields -->
                <div class="flex flex-col gap-8">
                    @for (
                        childField of asField(field).fields;
                        track childField
                    ) {
                        @if (
                            !asField(childField).hide &&
                            asField(childField).customField &&
                            asField(childField).label
                        ) {
                            <label
                                class="mb-1.5 block text-sm font-medium text-slate-700"
                            >
                                {{
                                    translateLabels
                                        ? (asField(childField).label
                                          | translate)
                                        : asField(childField).label
                                }}
                                @if (isRequired(childField)) {
                                    <span class="text-rose-500"> * </span>
                                }
                            </label>
                        }
                        @if (!asField(childField).hide) {
                            <ng-container
                                [ngTemplateOutlet]="
                                    $any(
                                        asField(childField).fields
                                            ? rowTemplate
                                            : asField(childField).customField
                                              ? asField(childField).customField
                                              : fieldTemplate
                                    )
                                "
                                [ngTemplateOutletContext]="{
                                    fields: asField(childField).fields,
                                    field: childField,
                                }"
                            ></ng-container>
                        }
                        @if (
                            !asField(childField).hide &&
                            asField(childField).customField &&
                            state.isInvalid(asField(childField).name)
                        ) {
                            <div
                                class="mt-1 text-xs text-rose-500"
                                style="display: block"
                            >
                                {{
                                    getErrorMessage(asField(childField).name)
                                        | async
                                }}
                            </div>
                        }
                    }
                </div>
            </div>
        </ng-template>
        <!-- Row Template -->
        <ng-template #rowTemplate let-fields="fields">
            <div
                class="flex flex-col gap-2 empty:hidden xl:flex-row xl:items-start"
            >
                @for (field of fields; track field) {
                    @if (
                        !asField(field).hide &&
                        asField(field).customField &&
                        asField(field).label
                    ) {
                        <label
                            class="mb-1.5 block text-sm font-medium text-slate-700"
                        >
                            {{
                                translateLabels
                                    ? (asField(field).label | translate)
                                    : asField(field).label
                            }}
                            @if (isRequired(field)) {
                                <span class="text-rose-500"> * </span>
                            }
                        </label>
                    }
                    @if (!asField(field).hide) {
                        <ng-container
                            [ngTemplateOutlet]="
                                $any(
                                    asField(field).fields
                                        ? rowTemplate
                                        : asField(field).customField
                                          ? asField(field).customField
                                          : fieldTemplate
                                )
                            "
                            [ngTemplateOutletContext]="{
                                fields: asField(field).fields,
                                field: field,
                            }"
                        ></ng-container>
                    }
                }
            </div>
        </ng-template>
        <!-- Field Template -->
        <ng-template #fieldTemplate let-field="field">
            <div
                [ngClass]="{
                    'self-center': asField(field).type === 'checkbox',
                    'md:col-span-2': asField(field).size === 12,
                }"
                [ngStyle]="{
                    'flex':
                        asField(field).size && asField(field).size !== 12
                            ? asField(field).size
                            : 'auto',
                }"
            >
                @if (
                    asField(field).type !== 'checkbox' && asField(field).label
                ) {
                    <label
                        class="mb-1.5 block text-sm font-medium text-slate-700"
                    >
                        {{
                            translateLabels
                                ? (asField(field).label | translate)
                                : asField(field).label
                        }}
                        @if (field?.help) {
                            <span
                                class="fa-light fa-circle-info ms-1 cursor-pointer text-slate-400"
                                data-toggle="tooltip"
                                [title]="
                                    $safeNavigationMigration(field?.help)
                                        | translate
                                "
                                data-placement="top"
                            ></span>
                        }
                        @if (isRequired(field)) {
                            <span class="text-rose-500"> * </span>
                        }
                    </label>
                }
                <ng-container
                    [ngTemplateOutlet]="
                        $any(
                            asField(field).customInputField
                                ? asField(field).customInputField
                                : ['text', 'password', 'number'].includes(
                                        asField(field).type
                                    )
                                  ? textInputTemplate
                                  : asField(field).type === 'email'
                                    ? emailInputTemplate
                                    : asField(field).type === 'textarea'
                                      ? textareaTemplate
                                      : asField(field).type === 'date'
                                        ? dateTemplate
                                        : asField(field).type === 'select'
                                          ? selectTemplate
                                          : asField(field).type === 'file'
                                            ? fileTemplate
                                            : asField(field).type === 'checkbox'
                                              ? checkboxTemplate
                                              : asField(field).type === 'color'
                                                ? colorTemplate
                                                : defaultTemplate
                        )
                    "
                    [ngTemplateOutletContext]="{ field: field }"
                >
                </ng-container>
                @if (asField(field).note) {
                    <small class="mt-1.5 block text-xs text-slate-500">
                        {{
                            translateLabels
                                ? (asField(field).note | translate)
                                : asField(field).note
                        }}
                    </small>
                }
                @if (state.isInvalid(asField(field).name)) {
                    <div class="mt-1.5 text-xs text-rose-500">
                        {{ getErrorMessage(asField(field).name) | async }}
                    </div>
                }
            </div>
        </ng-template>
        <!-- Text email Input Template -->
        <ng-template #emailInputTemplate let-field="field">
            <div class="mb-3 flex flex-row-reverse items-center">
                <div class="flex w-full flex-row" dir="ltr">
                    <!-- Input -->
                    <input
                        type="{{ asField(field).type }}"
                        name="{{ asField(field).name }}"
                        formControlName="{{ asField(field).name }}"
                        [ngClass]="{
                            'invalid': state.isInvalid(asField(field).name),
                            '!rounded-r-none rounded-l':
                                asField(field).emailSuffix &&
                                !state.group.controls[
                                    asField(field).name
                                ].value?.includes('@'),
                        }"
                        (change)="
                            asField(field).emailSuffix &&
                            !state.group.controls[
                                asField(field).name
                            ].value?.includes('@')
                                ? state.group.controls[
                                      asField(field).name
                                  ].setValue(
                                      $any($event.target).value +
                                          asField(field).emailSuffix
                                  )
                                : true
                        "
                        class="grow"
                    />
                    <!-- Suffix -->
                    @if (
                        asField(field).emailSuffix &&
                        !state.group.controls[
                            asField(field).name
                        ].value?.includes('@')
                    ) {
                        <div
                            class="flex shrink-0 flex-row items-center rounded-r bg-gray-200 px-2 text-gray-500"
                        >
                            {{ asField(field).emailSuffix }}
                        </div>
                    }
                </div>
            </div>
        </ng-template>
        <!-- Text Input Template -->
        <ng-template #textInputTemplate let-field="field">
            <div class="flex items-center gap-1">
                <input
                    [attr.dir]="getFieldDirection(asField(field))"
                    type="{{ asField(field).type }}"
                    name="{{ asField(field).name }}"
                    formControlName="{{ asField(field).name }}"
                    min="{{ asField(field).minValue }}"
                    max="{{ asField(field).maxValue }}"
                    [ngClass]="{
                        'invalid': state.isInvalid(asField(field).name),
                    }"
                    class="w-full"
                    (input)="
                        asField(field).isIntegerOnly ? onInput($event) : ''
                    "
                />
                @if (
                    asField(field).ai?.enableTextEnhancing &&
                    (!asField(field).type || asField(field).type === 'text')
                ) {
                    <ng-container
                        [ngTemplateOutlet]="aiEnhanceTextButtonTemplate"
                        [ngTemplateOutletContext]="{ field: field }"
                    />
                }
            </div>
        </ng-template>
        <!-- Textarea Template -->
        <ng-template #textareaTemplate let-field="field">
            <div class="flex items-center gap-1">
                <textarea
                    [attr.dir]="getFieldDirection(asField(field))"
                    class="w-full"
                    name="{{ asField(field).name }}"
                    formControlName="{{ asField(field).name }}"
                    [attr.rows]="asField(field).rows || null"
                    [ngClass]="{
                        'invalid': state.isInvalid(asField(field).name),
                    }"
                >
                </textarea>
                @if (asField(field).ai?.enableTextEnhancing) {
                    <ng-container
                        [ngTemplateOutlet]="aiEnhanceTextButtonTemplate"
                        [ngTemplateOutletContext]="{ field: field }"
                    />
                }
            </div>
        </ng-template>
        <!-- Date Template -->
        <ng-template #dateTemplate let-field="field">
            <div class="flex flex-row items-center gap-2">
                <input
                    #picker="appFlatpickr"
                    type="date"
                    appFlatpickr
                    class="w-full"
                    formControlName="{{ asField(field).name }}"
                    [config]="{
                        enableTime: asField(field).withTime,
                        dateFormat: asField(field).withTime
                            ? 'Y-m-d h:i K'
                            : 'Y-m-d',
                        enable: asField(field).enable || [],
                        disable: asField(field).disable || [],
                        minDate: asField(field).minDate || undefined,
                    }"
                    [ngClass]="{
                        'invalid': state.isInvalid(asField(field).name),
                    }"
                />
                @if (asField(field).isClearable) {
                    <button
                        type="button"
                        class="action-icon action-icon-primary shrink-0"
                        [disabled]="
                            state.group.get(asField(field).name).disabled
                        "
                        [appTooltip]="'translate_clear' | translate"
                        (click)="picker.clear()"
                    >
                        <i class="fa-light fa-times"></i>
                    </button>
                }
            </div>
        </ng-template>
        <!-- Select Template -->
        <ng-template #selectTemplate let-field="field">
            <ng-select
                (open)="asField(field).loader?.loadInitialList()"
                [addTag]="
                    asField(field).loader?.canCreateTagsWithBackend()
                        ? asField(field).loader.addTagPromise.bind(
                              asField(field).loader
                          )
                        : asField(field).canAddTags
                "
                [bindLabel]="asField(field).bindLabel"
                [bindValue]="
                    asField(field).bindValue ? asField(field).bindValue : null
                "
                [clearable]="!asField(field).notClearable"
                [compareWith]="asField(field).compareWith || defaultCompareWith"
                [formControlName]="asField(field).name"
                [hideSelected]="asField(field).hideSelected"
                [items]="
                    ($safeNavigationMigration(asField(field).loader?.items$)
                        | async) || asField(field).items
                "
                [loading]="
                    asField(field).loader?.itemsLoading ||
                    asField(field).loader?.addingTags.length > 0
                "
                [minTermLength]="asField(field).minTermLength || 0"
                [multiple]="asField(field).multiple"
                [ngClass]="{
                    'invalid': state.isInvalid(asField(field).name),
                }"
                [searchable]="!asField(field).notSearchable"
                [typeahead]="
                    $safeNavigationMigration(asField(field).loader?.itemInput$)
                "
                [groupBy]="asField(field).groupBy"
                [selectableGroup]="asField(field).selectableGroup"
                [selectableGroupAsModel]="asField(field).selectableGroupAsModel"
                [closeOnSelect]="!asField(field).multiple"
                appClearSearchOnSelect
            >
                <ng-template ng-option-tmp let-item="item">
                    <p>{{ item[asField(field).bindLabel] ?? item }}</p>
                    @if (asField(field).bindDescription) {
                        <p
                            class="whitespace-normal break-words text-sm text-gray-400"
                        >
                            {{ item[asField(field).bindDescription] }}
                        </p>
                    }
                </ng-template>
            </ng-select>
        </ng-template>
        <!-- File Template -->
        <ng-template #fileTemplate let-field="field">
            <div class="custom-file">
                <input
                    type="file"
                    class="custom-file-input"
                    id="{{ asField(field).name }}"
                    accept="{{ asField(field).mime || '*/*' }}"
                    [ngClass]="{
                        'invalid': state.isInvalid(asField(field).name),
                    }"
                    (change)="
                        loadFile(
                            $event,
                            state.group.controls[asField(field).name],
                            field
                        )
                    "
                />
                <label
                    class="custom-file-label"
                    for="{{ asField(field).name }}"
                    [ngClass]="{
                        'invalid': state.isInvalid(asField(field).name),
                    }"
                >
                    @if (asField(field).isFileObject) {
                        {{
                            state.group.controls[asField(field).name].value
                                ? state.group.controls[asField(field).name]
                                      .value.name
                                : ('translate_select_file' | translate)
                        }}
                    } @else {
                        {{
                            (state.group.controls[asField(field).name].value
                                ? 'translate_file_has_been_selected'
                                : 'translate_select_file'
                            ) | translate
                        }}
                    }
                </label>
            </div>
        </ng-template>
        <!-- Checkbox Template -->
        <ng-template #checkboxTemplate let-field="field">
            <app-toggle-switch
                formControlName="{{ asField(field).name }}"
                [label]="
                    translateLabels
                        ? (asField(field).label | translate)
                        : asField(field).label
                "
            ></app-toggle-switch>
        </ng-template>
        <!-- Checkbox Template -->
        <ng-template #colorTemplate let-field="field">
            <div class="flex flex-col gap-2">
                <!-- Label -->
                <input
                    formControlName="{{ asField(field).name }}"
                    [colorPicker]
                    (colorPickerChange)="
                        state.group.controls[asField(field).name].patchValue(
                            $event
                        )
                    "
                    [style.background]="
                        state.group.controls[asField(field).name].value
                    "
                    cpOutputFormat="hex"
                    cpAlphaChannel="disabled"
                    readonly
                    style="
                        width: 100%;
                        display: block;
                        border: 1px solid rgba(0, 0, 0, 0.2);
                        line-height: 1.7rem;
                    "
                />
            </div>
        </ng-template>
        <!-- Default Template -->
        <ng-template #defaultTemplate></ng-template>
        @for (field of state.fields; track field) {
            @if (!asField(field).hide) {
                <ng-container
                    [ngTemplateOutlet]="
                        $any(
                            asField(field).fields && asField(field).sectionTitle
                                ? sectionTemplate
                                : asField(field).fields
                                  ? rowTemplate
                                  : asField(field).customField
                                    ? asField(field).customField
                                    : fieldTemplate
                        )
                    "
                    [ngTemplateOutletContext]="{
                        fields: asField(field).fields,
                        field: field,
                    }"
                ></ng-container>
            }
        }
        <!-- In case of custom fields they need to be inside the form group -->
        <ng-content></ng-content>
    </form>
}

<!-- Ai Text Enhancing button -->
<ng-template #aiEnhanceTextButtonTemplate let-field="field">
    <button
        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-violet-50 hover:text-violet-600 disabled:cursor-not-allowed disabled:opacity-50"
        (click)="
            aiEnhanceText(asField(field).name, asField(field).ai.instructions)
        "
        [disabled]="
            aiState.currentlyProcessing ||
            !state.group.controls[asField(field).name].value
        "
        [appTooltip]="'translate_enhance_writing' | translate"
    >
        @if (aiState.currentlyProcessing !== asField(field).name) {
            <i class="fa-light fa-stars"></i>
        } @else {
            <app-loading-ring />
        }
    </button>
</ng-template>
